import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { PriceCategory } from "@/types/vehicleCard";
import { toast } from "sonner";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useListingManagement } from "@/hooks/useListingManagement";
import { cn } from "@/lib/utils";

type EditableInterval = {
  label: string;
  lowerBound: string;
  upperBound: string;
  color?: string;
};

interface EditListingPriceIntervalsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingId: string;
  priceCategories: PriceCategory[];
  useManualRatingIntervals?: boolean;
  onSaved: () => void | Promise<void>;
}

export function EditListingPriceIntervalsDialog({
  open,
  onOpenChange,
  listingId,
  priceCategories,
  useManualRatingIntervals,
  onSaved,
}: EditListingPriceIntervalsDialogProps) {
  const { isNative } = useNativePlatform();
  const {
    saveListingRatingIntervals,
    updateListingAsync,
    isSavingListingRatingIntervals,
    isUpdatingListing,
  } = useListingManagement({ enableGetAllListings: false });
  const [rows, setRows] = useState<EditableInterval[]>([]);
  const saving = isSavingListingRatingIntervals;
  const resetting = isUpdatingListing;

  useEffect(() => {
    if (!open) return;
    const sorted = [...priceCategories].sort(
      (a, b) => (b.dots ?? 0) - (a.dots ?? 0),
    );
    setRows(
      sorted.map((c) => ({
        label: c.label,
        lowerBound: String(c.lowerBound ?? ""),
        upperBound: String(c.upperBound ?? ""),
        color: c.color,
      })),
    );
  }, [open, priceCategories]);

  const updateRow = (
    index: number,
    field: "lowerBound" | "upperBound",
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const handleSave = async () => {
    const intervals: Array<{
      label: string;
      lowerBound: number;
      upperBound: number;
    }> = [];

    for (const row of rows) {
      const lowerBound = Number(String(row.lowerBound).replace(/\s/g, ""));
      const upperBound = Number(String(row.upperBound).replace(/\s/g, ""));
      if (!Number.isFinite(lowerBound) || !Number.isFinite(upperBound)) {
        toast.error(`Ogiltiga värden för ${row.label}`);
        return;
      }
      if (lowerBound > upperBound) {
        toast.error(`Nedre gräns får inte vara högre än övre för ${row.label}`);
        return;
      }
      intervals.push({ label: row.label, lowerBound, upperBound });
    }

    try {
      await saveListingRatingIntervals({ listingId, intervals });
      toast.success("Prisintervall sparade (manuella)");
      await onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Kunde inte spara prisintervall");
    }
  };

  const handleResetToAutoUncle = async () => {
    try {
      await updateListingAsync({
        id: listingId,
        useManualRatingIntervals: false,
      });
      toast.success("AutoUncle-intervall används igen");
      await onSaved();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Kunde inte återställa intervall");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:max-w-lg", isNative && "native-sheet-form")}
      >
        <DialogHeader>
          <DialogTitle>Redigera prisintervall</DialogTitle>
          <DialogDescription>
            Ändra intervallen manuellt. Efter sparning hämtas de inte längre
            från AutoUncle för den här annonsen.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
          {rows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              className="rounded-lg border border-border/60 p-3"
            >
              <Label className="text-sm font-semibold">{row.label}</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Nedre (kr)
                  </Label>
                  <Input
                    inputMode="numeric"
                    value={row.lowerBound}
                    onChange={(e) =>
                      updateRow(index, "lowerBound", e.target.value)
                    }
                    disabled={saving || resetting}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Övre (kr)
                  </Label>
                  <Input
                    inputMode="numeric"
                    value={row.upperBound}
                    onChange={(e) =>
                      updateRow(index, "upperBound", e.target.value)
                    }
                    disabled={saving || resetting}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            type="button"
            className="w-full"
            onClick={() => void handleSave()}
            disabled={saving || resetting || rows.length === 0}
          >
            {saving ? "Sparar…" : "Spara manuella intervall"}
          </Button>
          {useManualRatingIntervals && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => void handleResetToAutoUncle()}
              disabled={saving || resetting}
            >
              {resetting ? "Återställer…" : "Använd AutoUncle igen"}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => onOpenChange(false)}
            disabled={saving || resetting}
          >
            Avbryt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
